/**
 * Bilingual demo content. The seed writes every user-visible free-text string from
 * DEMO_TEXT (single source), and the UI calls `contentText(text, lang)` to show the
 * other language. Real user content is never in this map, so it is returned unchanged.
 */
type Pair = { fr: string; en: string };
const p = (fr: string, en: string): Pair => ({ fr, en });

export const DEMO_TEXT = {
  // ------------------------------------------------------------ names (properties, teams, sectors, clients)
  propTremblayHouse: p('Maison Saint-Hubert', 'Saint-Hubert House'),
  propTremblayCondo: p('Condo Plateau', 'Plateau Condo'),
  propHouse: p('Maison', 'House'),
  propRentalCondo: p('Condo locatif', 'Rental condo'),
  propApartment: p('Appartement', 'Apartment'),
  propClinic: p('Clinique', 'Clinic'),
  propMoveHouse: p('Maison (déménagement)', 'House (move-out)'),
  propCondo: p('Condo', 'Condo'),
  propSemiDetached: p('Jumelé', 'Semi-detached house'),
  teamAlpha: p('Équipe Alpha', 'Team Alpha'),
  teamBeta: p('Équipe Bêta', 'Team Beta'),
  teamGamma: p('Équipe Gamma', 'Team Gamma'),
  areaSouthShore: p('Rive-Sud', 'South Shore'),
  sectorMontrealCentre: p('Montréal Centre', 'Central Montréal'),
  clinicName: p('Clinique dentaire Sourire-Démo', 'Demo-Smile Dental Clinic'),
  tplStandardHouse: p('Standard - maison', 'Standard - house'),
  tplStandardApartment: p('Standard - appartement', 'Standard - apartment'),
  tplStandardCondo: p('Standard - condo', 'Standard - condo'),
  sourcePhone: p('téléphone', 'phone'),

  // ------------------------------------------------------------ property details
  tremblayPets: p('Chien (Max, golden retriever, très gentil)', 'Dog (Max, golden retriever, very friendly)'),
  tremblayParking: p('Stationnement dans l’entrée, côté gauche.', 'Park in the driveway, left side.'),
  tremblayNotes: p('Priorité salle de bain principale. Chien présent.', 'Main bathroom is the priority. Dog on site.'),
  tremblayForbidden: p('Ne pas entrer dans le bureau (2e étage, porte au fond du couloir).', 'Do not enter the office (2nd floor, door at the end of the hallway).'),
  tremblayPreferences: p('Produits sans parfum dans les chambres.', 'Fragrance-free products in the bedrooms.'),
  condoParking: p('Stationnement sur rue (vignette 132).', 'Street parking (permit 132).'),
  fortinPets: p('Deux chats', 'Two cats'),
  ouelletPets: p('Chien (berger allemand) — rester dans la cour', 'Dog (German shepherd) — stays in the yard'),
  clinicParking: p('Stationnement arrière réservé au personnel.', 'Rear parking reserved for staff.'),

  // ------------------------------------------------------------ access, notes, checklist adjustment
  accessLockbox: p('Boîte à clé (côté garage)', 'Key lockbox (garage side)'),
  accessLockboxNotes: p('Remettre la clé dans la boîte en partant.', 'Put the key back in the box when leaving.'),
  accessAlarm: p('Système d’alarme', 'Alarm system'),
  accessAlarmNotes: p('Panneau à droite de la porte d’entrée, 30 secondes pour désarmer.', 'Panel to the right of the front door, 30 seconds to disarm.'),
  noteEmployeeDog: p('Chien présent (Max) : fermer la porte du sous-sol avant de passer l’aspirateur.', 'Dog on site (Max): close the basement door before vacuuming.'),
  noteInternalSameTeam: p('Cliente très satisfaite de Julie et Marie : garder la même équipe.', 'Client very happy with Julie and Marie: keep the same crew.'),
  noteClientProducts: p('Les produits sans parfum sont fournis par la cliente (sous l’évier).', 'Fragrance-free products are supplied by the client (under the sink).'),
  taskMainBathroom: p('Salle de bain principale : détartrer la douche (priorité)', 'Main bathroom: descale the shower (priority)'),

  // ------------------------------------------------------------ quotes and agreements
  tremblaySpecialRequests: p('Priorité salle de bain principale. Ne pas entrer dans le bureau. Chien présent.', 'Main bathroom is the priority. Do not enter the office. Dog on site.'),
  lineBiweeklyHouse: p('Ménage standard aux deux semaines (maison 3 ch., 2 sdb)', 'Standard cleaning every two weeks (3-bed, 2-bath house)'),
  linePets: p('Animaux (chien)', 'Pets (dog)'),
  firstEstimate: p('Première estimation.', 'First estimate.'),
  tremblayQuoteMessage: p(
    'Bonjour Mme Tremblay, voici notre prix pour un ménage aux deux semaines le mardi à 10 h, avec Julie et Marie.',
    'Hello Mrs. Tremblay, here is our price for a cleaning every two weeks on Tuesdays at 10 a.m., with Julie and Marie.',
  ),
  petsSurchargeNote: p('Ajout du supplément animaux après vérification avec Julie.', 'Added the pet surcharge after checking with Julie.'),
  perfectThanks: p('Parfait, merci!', 'Perfect, thank you!'),
  lineWeekly12Months: p('Ménage hebdomadaire, contrat 12 mois', 'Weekly cleaning, 12-month contract'),
  acceptedByPhone: p('Accepté par téléphone.', 'Accepted over the phone.'),
  lineWeeklyClinic: p('Entretien commercial hebdomadaire (clinique)', 'Weekly commercial cleaning (clinic)'),
  deepBeforeTenants: p('Grand ménage avant l’arrivée de locataires.', 'Deep clean before new tenants move in.'),
  lineDeepCondo: p('Nettoyage profond (condo 2 ch.)', 'Deep cleaning (2-bed condo)'),
  lineOven: p('Intérieur du four', 'Inside oven'),
  lineFridge: p('Intérieur du réfrigérateur', 'Inside fridge'),
  deepQuoteMessage: p('Voici le prix pour le grand ménage du condo, four et réfrigérateur inclus.', 'Here is the price for the condo deep clean, oven and fridge included.'),
  requestByPhone: p('Demande reçue par téléphone.', 'Request received over the phone.'),
  lineWeekly: p('Ménage hebdomadaire', 'Weekly cleaning'),
  priceTooHigh: p('Trouve le prix trop élevé pour une visite par semaine.', 'Finds the price too high for one visit a week.'),
  lineDeep: p('Nettoyage profond', 'Deep cleaning'),

  // ------------------------------------------------------------ jobs, dispatch, absences, payments
  demoOverride: p('Données de démonstration', 'Demo data'),
  cancelledTravel: p('Annulé par la cliente (voyage)', 'Cancelled by the client (travelling)'),
  cancelNotePhone: p('Demande reçue par téléphone', 'Request received over the phone'),
  clientHomeToday: p('Le client sera présent, il travaille de la maison.', 'The client will be home, he works from home.'),
  medicalAppointment: p('Rendez-vous médical', 'Medical appointment'),
  clinicSubscription: p('Abonnement mensuel - entretien clinique', 'Monthly subscription - clinic cleaning'),
  monthlySubscription: p('Abonnement mensuel', 'Monthly subscription'),

  // ------------------------------------------------------------ conversations
  convOvenSubject: p('Ajout du four au prochain ménage', 'Adding the oven to the next cleaning'),
  convOvenBody: p('Bonjour, serait-il possible d’ajouter le nettoyage du four lors du prochain ménage? Merci!', 'Hello, could you add cleaning the oven at the next visit? Thanks!'),
  convOvenInternal: p('@Sophie vérifier le prix avec Julie (four + 30 min).', '@Sophie check the price with Julie (oven + 30 min).'),
  convKeySubject: p('Clé chez M. Ouellet', 'Key at Mr. Ouellet’s'),
  convKeyBody: p('Bonjour, M. Ouellet sera présent aujourd’hui ou je dois passer par la boîte à clé?', 'Hello, will Mr. Ouellet be home today or should I use the lockbox?'),
  convKeyReply: p('Il sera présent, il travaille de la maison. Bonne journée Julie!', 'He will be home, he works from home. Have a good day Julie!'),
  // Written in English by the English-speaking client (contentText also maps EN -> FR).
  convInvoiceSubject: p('Facture', 'Invoice'),
  convInvoiceBody: p('Bonjour, pourriez-vous m’envoyer la facture de la semaine dernière? Merci!', 'Hi, could you send me the invoice for last week? Thanks!'),
  convInvoiceReply: p('Bien sûr, elle est maintenant disponible dans votre page Paiements.', 'Of course, it is now available in your Payments page.'),
} as const satisfies Record<string, Pair>;

const FR_EN = new Map<string, string>();
const EN_FR = new Map<string, string>();
for (const { fr, en } of Object.values(DEMO_TEXT) as Pair[]) {
  if (!FR_EN.has(fr)) FR_EN.set(fr, en);
  if (!EN_FR.has(en)) EN_FR.set(en, fr);
}

/** System-generated labels with a variable part (payment descriptions). */
const PATTERNS: { fr: RegExp; en: RegExp; toEn: (m: RegExpMatchArray) => string; toFr: (m: RegExpMatchArray) => string }[] = [
  { fr: /^Ménage #(\d+)$/, en: /^Cleaning #(\d+)$/, toEn: (m) => `Cleaning #${m[1]}`, toFr: (m) => `Ménage #${m[1]}` },
  { fr: /^Paie - (\d+) ménage\(s\)$/, en: /^Pay - (\d+) cleaning\(s\)$/, toEn: (m) => `Pay - ${m[1]} cleaning(s)`, toFr: (m) => `Paie - ${m[1]} ménage(s)` },
];

/**
 * Returns the demo string in the requested language when it is a known demo text
 * (FR -> EN or EN -> FR); any other text (real user content) is returned unchanged.
 */
export function contentText(text: string | null | undefined, lang: 'fr' | 'en'): string {
  if (text === null || text === undefined) return '';
  const exact = (lang === 'en' ? FR_EN : EN_FR).get(text);
  if (exact !== undefined) return exact;
  for (const pt of PATTERNS) {
    const m = text.match(lang === 'en' ? pt.fr : pt.en);
    if (m) return lang === 'en' ? pt.toEn(m) : pt.toFr(m);
  }
  return text;
}
